import { useState } from 'react';
import type { ReactNode } from 'react';
import { cardClasses } from './Card';
import { Icon } from './Icon';

export interface CollapsiblePanelProps {
  /** Libellé affiché sur le bouton de bascule (peut inclure un compteur, ex. « Historique des réalisations (3) »). */
  label: ReactNode;
  /** `aria-label` de la section — texte stable, sans compteur variable, distinct de `label`. */
  sectionLabel: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * Panneau repliable (audit UX/UI passe 2, B1/I6) : composant partagé pour un
 * motif jusque-là dupliqué dans deux implémentations JSX distinctes, avec un
 * risque de dérive visuelle entre elles (`DataBackup.tsx` et
 * `CookLogHistory` dans `RecipeDetail.tsx`). État contrôlé plutôt que
 * `<details>`/`<summary>` — comportement de bascule plus prévisible en test
 * (jsdom) qu'un élément natif, cf. commentaire d'origine de `CookLogHistory`.
 */
export function CollapsiblePanel({ label, sectionLabel, defaultOpen = false, children }: CollapsiblePanelProps): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section aria-label={sectionLabel} className={cardClasses({ className: 'text-sm text-foreground' })}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => { setOpen((current) => !current); }}
        // `aria-expanded` manquait : le bouton ne disait pas qu'il déplie
        // quelque chose, ni si c'était déjà fait.
        className="flex min-h-touch w-full items-center justify-between gap-3 text-left font-medium"
      >
        {label}
        <Icon name={open ? 'moins' : 'plus'} className="text-foreground-muted" />
      </button>
      {/* **Monté seulement une fois ouvert**, et non déplié par
          `.disclosure` (index.css). Le dépliage animé exige que le contenu
          reste dans le document ; or ces panneaux contiennent des champs de
          formulaire, et deux garanties du produit l'interdisent : la fiche en
          lecture « ne contient aucun champ de formulaire » (US-14), et la
          restauration de sauvegarde « n'apparaît qu'après un clic ». En
          navigateur réel, `visibility: hidden` les retirerait bien de l'ordre
          de tabulation — mais la garantie porte sur le document, pas sur ce
          qui est atteignable, et c'est la bonne façon de l'écrire : un champ
          qu'on ne veut pas offrir ne doit pas exister.

          L'animation cède donc devant la garantie. Ce qui reste de la passe
          mouvement ici : l'état est annoncé (`aria-expanded`) et dessiné
          (l'icône), ce qui n'était le cas ni de l'un ni de l'autre. */}
      {open && <div className="pt-3">{children}</div>}
    </section>
  );
}
